import { useShallow } from 'zustand/react/shallow';

import { toastService } from '@thefarmersfront/kitchen-mobile';

import { TOAST_MESSAGES } from '~/constants';
import { useUserInfoStore, useWorkInfoStore } from '~/store';
import { initialWorkInfoState } from '~/store/workInfo/useWorkInfoStore';
import { type TypeErrorFormat, defaultErrorHandler, errorFormatter } from '~/utils';

import { useGetCommuteInfo, useGetUserLatestContract, useUserInfo } from '../api';
import useGetTodayWorkTimeline from '../api/useGetTodayWorkTimeline';

export const useGetLatestUserAndCommuteInfo = () => {
  const requestGetUserInfo = useUserInfo();
  const requestGetCommuteInfo = useGetCommuteInfo();
  const requestUserLatestContract = useGetUserLatestContract();
  const requestGetTodayWorkTimeline = useGetTodayWorkTimeline();

  const { setUserInfo, latestContract, getIsNeedSafetyTraining } = useUserInfoStore(
    useShallow((state) => ({
      setUserInfo: state.actions.setUserInfo,
      latestContract: state.latestContract,
      getIsNeedSafetyTraining: state.actions.getIsNeedSafetyTraining,
    }))
  );
  const { setWorkInfo, setTodayWorkTimeline } = useWorkInfoStore(
    useShallow((state) => ({
      setWorkInfo: state.actions.setWorkInfo,
      setTodayWorkTimeline: state.actions.setTodayWorkTimeline,
    }))
  );

  /** 안전교육 대상 여부 */
  const isNeedSafetyTraining = getIsNeedSafetyTraining();

  const requestUserInfo = async () => {
    await requestGetUserInfo({
      onSuccess: (userInfo) => {
        if (isNeedSafetyTraining && userInfo.safetyEducation?.status?.code === 'COMPLETED') {
          toastService.addToast({
            message: TOAST_MESSAGES.COMPLETED_SAFETY_EDUCATION,
          });
        }

        setUserInfo({
          ...userInfo,
          kurlyContract: userInfo.kurlyContract,
          status: userInfo.status,
          privacyInfo: userInfo.privacyInfo,
          contractAvailableStatus: userInfo.contractAvailableStatus,
          latestContractSignedAt: userInfo.latestContractSignedAt,
          safetyEducation: userInfo.safetyEducation,
          specialMedicalExamination: userInfo.specialMedicalExamination,
        });

        if (latestContract === null || latestContract?.cluster === null) {
          requestUserLatestContract({
            onSuccess: (response) => {
              if (response) {
                setUserInfo({ latestContract: response.data });
              }
            },
            onError: (error) => {
              throw error;
            },
          });
        }
      },
    });
  };

  const requestCommuteInfo = async () => {
    await requestGetCommuteInfo({
      onSuccess: (response) => {
        const commuteInfo = response?.data;

        setUserInfo({ earlyEndWork: commuteInfo.status?.code === 'EARLY_END_WORK' });

        let newWorkInfo = {
          ...(commuteInfo?.data ?? initialWorkInfoState),
          status: commuteInfo.status,
          recentCheckInOut: commuteInfo.recentCheckInOut,
          workType: commuteInfo.workType,
        };

        if (commuteInfo.status?.code !== 'START_WORK') {
          setWorkInfo(newWorkInfo);
          return;
        }

        /** 출근 중인 경우에만 오늘의 이력 타임라인 정보를 갱신 */
        requestGetTodayWorkTimeline({
          onSuccess: (response) => {
            if (response) {
              newWorkInfo = {
                ...newWorkInfo,
                lateMinutes: response.data.lateMinutes,
              };
              setWorkInfo(newWorkInfo);
              setTodayWorkTimeline(response.data.workdayTimeline);
            }
          },
          onError: (error) => {
            throw error;
          },
        });
      },
      onError: (error) => {
        throw error;
      },
    });
  };

  /** 유저 정보, 출근/체크인 정보, 오늘의 이력 타임라인 정보를 갱신 */
  const getLatestUserAndCommuteInfo = async (): Promise<void> => {
    try {
      await requestUserInfo();
      await requestCommuteInfo();
    } catch (error) {
      const isFormattedError = error !== null && typeof error === 'object' && 'showErrorToast' in error;
      defaultErrorHandler(isFormattedError ? (error as TypeErrorFormat) : errorFormatter(error));
    }
  };

  return getLatestUserAndCommuteInfo;
};
